/**
 * Breadcrumb Generation Utility
 * Generates breadcrumb trails from route paths, supporting both static and dynamic routes
 */

import { getRouteConfig, type RouteConfig } from '@/app/router/routeConfig';

export interface BreadcrumbItem {
  label: string;
  path: string;
  isCurrentPage?: boolean;
}

/**
 * Resolves a breadcrumb label, optionally invoking a custom resolver with loader data.
 * Extracted to avoid deeply nested try/catch blocks.
 */
function resolveBreadcrumbLabel(
  config: RouteConfig,
  loaderData: unknown,
  pathForWarn: string
): string {
  let label = config.label;
  if (config.breadcrumbResolver && loaderData) {
    try {
      label = config.breadcrumbResolver(loaderData as Record<string, unknown>);
    } catch (error) {
      console.warn('Breadcrumb resolver failed for', pathForWarn, error);
    }
  }
  return label;
}

/**
 * Generates breadcrumb trail from current pathname
 * Supports multi-level breadcrumbs and dynamic routes
 *
 * @param pathname - Current route pathname (e.g., '/help/articles/123')
 * @param loaderData - Optional loader data for dynamic label resolution
 * @returns Array of breadcrumb items
 *
 * @example
 * // Static route
 * generateBreadcrumbs('/help')
 * // => [{ label: 'Home', path: '/dashboard' }, { label: 'Help Center', path: '/help' }]
 *
 * // Dynamic route
 * generateBreadcrumbs('/help/articles/123', { article: { title: 'Getting Started' } })
 * // => [
 * //   { label: 'Home', path: '/dashboard' },
 * //   { label: 'Help Center', path: '/help' },
 * //   { label: 'Getting Started', path: '/help/articles/123' }
 * // ]
 */
export function generateBreadcrumbs(
  pathname: string,
  loaderData?: unknown
): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [];

  // Always start with Home (Dashboard)
  breadcrumbs.push({
    label: 'Home',
    path: '/dashboard',
    isCurrentPage: pathname === '/dashboard',
  });

  // Don't show breadcrumbs for home page or auth pages
  if (
    pathname === '/' ||
    pathname === '/dashboard' ||
    pathname.startsWith('/login') ||
    pathname.startsWith('/register') ||
    pathname.startsWith('/forgot-password') ||
    pathname.startsWith('/reset-password')
  ) {
    return breadcrumbs;
  }

  // Get route config for current path
  const currentConfig = getRouteConfig(pathname);

  if (!currentConfig) {
    // If no exact match, this might be a dynamic route
    // Try to build breadcrumbs from path segments
    return buildDynamicBreadcrumbs(pathname, loaderData, breadcrumbs);
  }

  // Build breadcrumb trail using parent relationships
  const trail: RouteConfig[] = [];
  let config: RouteConfig | undefined = currentConfig;

  while (config) {
    trail.unshift(config);
    config = config.parent ? getRouteConfig(config.parent) : undefined;
  }

  // Convert trail to breadcrumb items
  for (const item of trail) {
    // Skip if this is the home/dashboard (already added)
    if (item.path === '/dashboard') continue;

    // Resolve dynamic label if breadcrumbResolver exists
    let label = item.label;
    if (item.breadcrumbResolver && loaderData) {
      try {
        label = item.breadcrumbResolver(loaderData as Record<string, unknown>);
      } catch (error) {
        console.warn('Breadcrumb resolver failed:', error);
        // Fallback to static label
      }
    }

    breadcrumbs.push({
      label,
      path: item.path,
      isCurrentPage: item.path === pathname,
    });
  }

  return breadcrumbs;
}

/**
 * Builds breadcrumbs for dynamic routes by analyzing path segments
 * Used when no exact route config match is found
 */
function buildDynamicBreadcrumbs(
  pathname: string,
  loaderData: unknown,
  breadcrumbs: BreadcrumbItem[]
): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  let currentPath = '';

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    currentPath += `/${segment}`;

    // Try to get config for this path segment
    const config = getRouteConfig(currentPath);

    if (config) {
      // Found matching config
      const label = resolveBreadcrumbLabel(config, loaderData, currentPath);

      breadcrumbs.push({
        label,
        path: currentPath,
        isCurrentPage: currentPath === pathname,
      });
    } else {
      // No config found - this might be a dynamic segment (e.g., :id)
      // Try to build a pattern path (e.g., /help/articles/:id)
      const patternPath = buildPatternPath(segments, i);
      const patternConfig = getRouteConfig(patternPath);

      if (patternConfig) {
        const label = resolveBreadcrumbLabel(
          patternConfig,
          loaderData,
          patternPath
        );

        breadcrumbs.push({
          label,
          path: currentPath, // Use actual path, not pattern
          isCurrentPage: currentPath === pathname,
        });
      } else {
        // Fallback: use segment as label (capitalize first letter)
        const label = segment.charAt(0).toUpperCase() + segment.slice(1);
        breadcrumbs.push({
          label,
          path: currentPath,
          isCurrentPage: currentPath === pathname,
        });
      }
    }
  }

  return breadcrumbs;
}

/**
 * Builds a pattern path from segments (e.g., ['help', 'articles', '123'] => '/help/articles/:id')
 */
function buildPatternPath(segments: string[], currentIndex: number): string {
  const patternSegments = segments
    .slice(0, currentIndex + 1)
    .map((seg, idx) => {
      // If segment looks like an ID (all digits or UUID-like), replace with :id
      if (idx === currentIndex && (isNumeric(seg) || isUUID(seg))) {
        return ':id';
      }
      return seg;
    });

  return `/${patternSegments.join('/')}`;
}

/**
 * Checks if a string is numeric
 */
function isNumeric(str: string): boolean {
  return /^\d+$/.test(str);
}

/**
 * Checks if a string looks like a UUID or hash
 */
function isUUID(str: string): boolean {
  return (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      str
    ) || str.length > 20
  );
}

/**
 * Gets the parent breadcrumb path for a given pathname
 * @param pathname - Current pathname
 * @returns Parent path or null if no parent
 */
export function getParentPath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length <= 1) return null;

  segments.pop();
  return `/${segments.join('/')}`;
}
